// Pure helpers of the translations (no React), shared with the unit tests.

/** {name} placeholders. Unknown placeholders stay visible, so a missing value is noticed. */
export function interpolate(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

export function formatBytes(value: number, language = 'en'): string {
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < UNITS.length - 1) {
    amount /= 1024;
    unit++;
  }
  const digits = unit === 0 || amount >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(language, { maximumFractionDigits: digits }).format(amount)} ${UNITS[unit]}`;
}

/** A piece of a text with links in it: plain words, or a link's name and its words. */
export type TextPart = string | { link: string; text: string };

/**
 * A translated sentence with links in it (TF-388): "I accept the {terms}Terms of service{/terms}."
 * Each language words the whole sentence, so a link takes the grammar around it and its punctuation
 * stays beside it. Plain text before, between and after; an unclosed tag stays visible as text.
 */
export function linkParts(text: string): TextPart[] {
  const parts: TextPart[] = [];
  const lastClosing = closingTags(text);
  let kept = 0; // the text before this is in parts
  let from = 0;
  for (let open = text.indexOf('{', from); open >= 0; open = text.indexOf('{', from)) {
    const nameEnd = wordEnd(text, open + 1);
    from = nameEnd > open + 1 ? nameEnd : open + 1;
    if (nameEnd === open + 1 || text[nameEnd] !== '}') continue;
    const name = text.slice(open + 1, nameEnd);
    // Looked up first: a search that finds nothing would read to the end again for every tag.
    if ((lastClosing.get(name) ?? -1) <= nameEnd) continue;
    const closing = `{/${name}}`;
    const close = text.indexOf(closing, nameEnd + 1);
    if (open > kept) parts.push(text.slice(kept, open));
    parts.push({ link: name, text: text.slice(nameEnd + 1, close) });
    kept = close + closing.length;
    from = kept;
  }
  if (kept < text.length) parts.push(text.slice(kept));
  return parts;
}

/** Where a run of letters, digits and underscores from `at` ends. */
function wordEnd(text: string, at: number): number {
  let end = at;
  while (end < text.length && /\w/.test(text.charAt(end))) end += 1;
  return end;
}

/** Each name's last {/name} in the text. */
function closingTags(text: string): Map<string, number> {
  const last = new Map<string, number>();
  for (let at = text.indexOf('{/'); at >= 0; at = text.indexOf('{/', at + 2)) {
    const end = wordEnd(text, at + 2);
    if (end > at + 2 && text[end] === '}') last.set(text.slice(at + 2, end), at);
  }
  return last;
}
